import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { MapPinOff, Home, ArrowLeft } from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <>
      <Seo
        title="404 - Page Not Found | TotoWala"
        description="The page you're looking for doesn't exist or has been moved. Let's get you back on track."
        noIndex={true}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg flex flex-col">
        <HeaderSection />

        <main className="flex-1 flex flex-col items-center justify-center bg-surface-alt dark:bg-dark-surface-alt py-24 px-4">
          <div className="max-w-[760px] mx-auto text-center flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="w-20 h-20 rounded-control bg-white dark:bg-dark-surface border border-line dark:border-dark-line flex items-center justify-center mb-8 shadow-sm"
            >
              <MapPinOff size={32} className="text-primary" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="font-display text-5xl sm:text-7xl md:text-8xl font-semibold leading-tight text-ink dark:text-dark-ink mb-2"
            >
              404
            </motion.h1>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="font-display text-xl sm:text-2xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              Oops! Page Not Found
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-base sm:text-lg text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto mb-8"
            >
              The page you're looking for doesn't exist or has been moved.
              Let's get you back on track!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="flex items-center justify-center gap-4 flex-wrap mb-12"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-primary hover:bg-primary-dark transition-colors"
              >
                <Home size={16} />
                Go to Homepage
              </Link>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-ink dark:text-dark-ink border border-line dark:border-dark-line hover:border-primary transition-colors bg-white dark:bg-dark-surface"
              >
                <ArrowLeft size={16} />
                Go Back
              </button>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="bg-white dark:bg-dark-surface rounded-card p-6 border border-line dark:border-dark-line w-full max-w-md shadow-sm"
            >
              <p className="text-xs sm:text-sm font-bold text-ink-soft dark:text-dark-ink-soft mb-4 uppercase tracking-wider">Quick Links</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/about"
                  className="text-sm px-4 py-2 rounded-control bg-surface-alt dark:bg-dark-bg text-ink dark:text-dark-ink hover:text-primary transition-colors"
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  className="text-sm px-4 py-2 rounded-control bg-surface-alt dark:bg-dark-bg text-ink dark:text-dark-ink hover:text-primary transition-colors"
                >
                  Contact
                </Link>
                <Link
                  to="/help"
                  className="text-sm px-4 py-2 rounded-control bg-surface-alt dark:bg-dark-bg text-ink dark:text-dark-ink hover:text-primary transition-colors"
                >
                  Help Center
                </Link>
                <a
                  href="/sitemap.xml"
                  className="text-sm px-4 py-2 rounded-control bg-surface-alt dark:bg-dark-bg text-ink dark:text-dark-ink hover:text-primary transition-colors"
                >
                  Sitemap
                </a>
                <a
                  href="/llms.txt"
                  className="text-sm px-4 py-2 rounded-control bg-surface-alt dark:bg-dark-bg text-ink dark:text-dark-ink hover:text-primary transition-colors"
                >
                  llms.txt
                </a>
              </div>
            </motion.div>

            {/* For crawlers/agents that don't render the styling above */}
            <p className="sr-only">
              This page does not exist. See the sitemap at /sitemap.xml or the
              agent-readable site overview at /llms.txt.
            </p>

          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default NotFound;
